import { Link } from "react-router-dom";
import { Code, Database, Cloud, Lock, GitBranch, Workflow, Check, ArrowRight, Cpu } from "lucide-react";
import Navbar from "../../components/Navbar";
import PageMeta from "../../components/PageMeta";
import Footer from "../../components/Footer";
import FloatingFAQ from "../../components/FloatingFAQ";
import ConsultationForm from "../../components/ConsultationForm";

const features = [
  { icon: Cpu, title: "Scalable Architecture", description: "Built to grow with your business." },
  { icon: Database, title: "Data Management", description: "Robust database solutions for your data." },
  { icon: Cloud, title: "Cloud Integration", description: "Seamless AWS, Google Cloud, and Azure integration." },
  { icon: Lock, title: "Enterprise Security", description: "Bank-level security with encryption." },
  { icon: GitBranch, title: "Version Control", description: "Professional development workflows with Git." },
  { icon: Workflow, title: "Process Automation", description: "Automate repetitive tasks and workflows." }
];

const solutions = [
  { title: "CRM Systems", description: "Custom customer relationship management tools.", features: ["Contact Management", "Sales Pipeline", "Reporting", "Email Integration"] },
  { title: "Inventory Management", description: "Track stock levels and manage orders.", features: ["Real-time Tracking", "Automated Reordering", "Multi-location", "Barcode Scanning"] },
  { title: "Business Dashboards", description: "Visualize your key metrics.", features: ["Custom KPIs", "Real-time Data", "Interactive Charts", "Export Reports"] },
  { title: "API Development", description: "Connect your systems.", features: ["RESTful APIs", "GraphQL", "Webhooks", "Documentation"] }
];

const softwareProcess = [
  { step: "01", title: "Requirements", description: "We analyze your business processes and goals." },
  { step: "02", title: "Architecture", description: "Our architects design a scalable system." },
  { step: "03", title: "Development", description: "Agile development with regular demos." },
  { step: "04", title: "Deployment", description: "Smooth rollout with training and support." }
];

const integrations = ["Salesforce", "HubSpot", "QuickBooks", "Stripe", "PayPal", "Slack", "Microsoft 365", "Google Workspace", "Zapier", "Twilio", "SendGrid", "AWS"];

export default function CustomSoftware() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Custom Software Development | Averoan Technologies"
        description="CRM systems, dashboards, APIs, and business automation built for your workflow."
      />
      <Navbar />

      <section className="pt-32 pb-16 relative" data-testid="software-hero">
        <div className="hero-glow absolute top-0 left-0 opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl">
            <span className="tag mb-6">Custom Software</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Software built<br /><span style={{ color: '#D4FF00' }}>for you</span>
            </h1>
            <p className="text-base md:text-lg max-w-xl mb-8" style={{ color: '#A1A1AA' }}>
              Stop adapting your business to off-the-shelf software. We build custom solutions that scale with your growth.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#get-started" className="btn-primary" data-testid="software-cta">Discuss Your Project <ArrowRight size={16} /></a>
              <Link to="/portfolio" className="btn-outline">See Our Work</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Capabilities</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Enterprise-grade quality</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="card">
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}>
                    <feature.icon size={22} style={{ color: '#D4FF00' }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{feature.title}</h3>
                  <p className="text-sm" style={{ color: '#A1A1AA' }}>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Solutions</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>What we build</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {solutions.map((solution) => (
              <div key={solution.title} className="p-8 rounded-xl" style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h3 className="text-xl font-bold mb-3 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{solution.title}</h3>
                <p className="mb-6" style={{ color: '#A1A1AA' }}>{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm" style={{ color: '#A1A1AA' }}>
                      <Check size={14} style={{ color: '#D4FF00' }} />{feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Development Process</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>From idea to launch</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {softwareProcess.map((item) => (
              <div key={item.step}>
                <div className="text-5xl font-bold mb-4" style={{ fontFamily: '"Clash Display", sans-serif', color: '#D4FF00' }}>{item.step}</div>
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="tag mb-4">Integrations</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Connect everything</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {integrations.map((integration) => (
              <span key={integration} className="px-6 py-3 rounded-full text-sm" style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)', color: '#A1A1AA' }}>{integration}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="get-started" className="section-padding" style={{ background: '#18181B' }} data-testid="software-consultation">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="tag mb-4">Get Started</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Let's build your <span style={{ color: '#D4FF00' }}>solution</span>
            </h2>
            <p className="text-base md:text-lg mt-4" style={{ color: '#A1A1AA' }}>
              Tell us about your challenges and book a free consultation.
            </p>
          </div>
          <ConsultationForm preSelectedServiceId="software" />
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
